/**
 * Forgot Password Page Component
 * 
 * Handles password recovery flow including:
 * - Request password reset via email
 * - Verify reset code and set new password
 * 
 * Uses URL search params to determine which form to show.
 */

import { Paper, Stack, Typography } from "@mui/material";
import React from "react";
import ForgotPasswordForm from "./components/forgot-password-form";
import ResetPasswordForm from "./components/reset-password-form";
import { AuthContext } from "../../context/auth-context";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClearCallLogoTitle } from "../../components/clear-call-logo";
import Copyright from "../../components/copyright";
import ribbonBackground from "../../assets/ribbon.png";

const ForgotPasswordPage: React.FC = () => {
  // Extract email from URL search params if present
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const { resetPassword, confirmForgotPassword } = React.useContext(AuthContext);
  const navigate = useNavigate();

  /**
   * Handles password reset request
   * @param email - User's email address for password reset
   */
  const forgotPasswordHandler = async (email: string) => {
    console.log("Requesting password reset for:", email);

    resetPassword(email, {
      onSuccess: () => {
        console.log("Password reset email sent");
        toast.success("Password reset email sent", { duration: 5000 });
        // Navigate to same page with email parameter to show reset form
        navigate("/forgotpassword?email=" + email);
      },
      onFailure: (error) => {
        console.error("Password reset email not sent", error);
        toast.error("Password reset email not sent", { duration: 5000 });
      },
    });
  };

  /**
   * Handles password reset confirmation with verification code
   * @param verificationCode - Email verification code
   * @param newPassword - New password to set
   */
  const confirmForgotPasswordHandler = async (
    verificationCode: string,
    newPassword: string
  ) => {
    console.log("Confirming password reset");

    confirmForgotPassword(email, verificationCode, newPassword, {
      onSuccess: (success) => {
        console.log("Password reset successful:", success);
        navigate("/login");
      },
      onFailure: (error) => {
        console.error("Password reset failed", error);
        toast.error("Password reset failed", { duration: 5000 });
      },
    });
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        backgroundImage:
          `linear-gradient(160deg, rgba(0, 99, 102, 0.73), rgba(0, 76, 131, 0.93)), url(${ribbonBackground})`,
        backgroundBlendMode: "normal",
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Paper
        elevation={20}
        sx={{
          width: 400,
          p: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          background:
            "linear-gradient(145deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.28) 100%)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow:
            "0 24px 60px rgba(8, 39, 69, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.35)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <Stack spacing={3} alignItems="center">
          <ClearCallLogoTitle size={150} />
          
          {/* Show reset form if email is provided, otherwise show forgot password form */}
          {email.length > 0 ? (
            <ResetPasswordForm resetPassword={confirmForgotPasswordHandler} />
          ) : (
            <ForgotPasswordForm
              forgotPassword={forgotPasswordHandler}
              intialEmail=""
            />
          )}
          
          {/* Back to login link */}
          <Typography
            variant="body2"
            color="common.white"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": {
                opacity: 0.8,
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate("/login")}
          >
            Back to Login
          </Typography>
        </Stack>
      </Paper>
      <Copyright color="rgba(255, 255, 255, 0.7)" sx={{ mt: 5 }} />
    </Stack>
  );
};

export default ForgotPasswordPage;
